import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from './ApiConfig';

/**
 * VideoPlayer Component
 * Displays a video player for answers that have an associated video file
 * 
 * @param {string} file_key - The file key to fetch the signed URL for
 */
const VideoPlayer = ({ file_key }) => {
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSignedUrl = async () => {
      if (!file_key) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await fetch(API_ENDPOINTS.SIGN_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ file_key: file_key })
        });

        if (!response.ok) {
          throw new Error(`Failed to sign URL: ${response.status}`);
        }

        const data = await response.json();
        const signedUrl = data[0]?.signed_url;

        if (!signedUrl) {
          throw new Error('No signed URL returned from server');
        }

        setVideoUrl(signedUrl);
      } catch (err) {
        console.error('Error fetching signed video URL:', err);
        setError('Failed to load video');
      } finally {
        setLoading(false);
      }
    };

    fetchSignedUrl();
  }, [file_key]);

  if (!file_key) {
    return null;
  }

  if (loading) {
    return (
      <div className="mt-2 p-3 bg-blue-50 rounded-md flex items-center gap-2">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
        <span className="text-sm text-gray-600">Loading video...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-2 p-3 bg-red-50 rounded-md">
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  if (!videoUrl) {
    return null;
  }

  return (
    <div className="mt-2 p-3 bg-blue-50 rounded-md">
      <video 
        controls 
        className="w-full rounded-lg"
        style={{ maxWidth: '100%', maxHeight: '500px' }}
        preload="metadata"
      >
        <source src={videoUrl} type="video/webm" />
        <source src={videoUrl} type="video/mp4" />
        <source src={videoUrl} type="video/ogg" />
        Your browser does not support the video element.
      </video>
    </div>
  );
};

export default VideoPlayer;
